"use client"

import { useKonamiCode } from "@/hooks/useKonamiCode";
import dictionary from "@/utils/dictionary";
import { haptics } from "@/utils/haptics";
import { ReactNode, useCallback, useEffect, useRef, useState } from "react";

interface Props {
    children: ReactNode;
}

export default function LayoutWrapper({ children }: Props) {
    const [konamiActive, setKonamiActive] = useState(false);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        // Unlock AudioContext for iOS Safari on the very first screen interaction
        const handleUnlock = () => {
            haptics.unlock();
            window.removeEventListener("touchstart", handleUnlock, true);
            window.removeEventListener("mousedown", handleUnlock, true);
        };

        window.addEventListener("touchstart", handleUnlock, true);
        window.addEventListener("mousedown", handleUnlock, true);

        const handleMouseEnter = (e: MouseEvent) => {
            const target = e.target;
            if (target instanceof Element) {
                if (target.tagName === "A" || target.tagName === "BUTTON" || target.getAttribute("role") === "button") {
                    haptics.hover();
                }
            }
        };

        const handleClick = (e: MouseEvent) => {
            const target = e.target;
            if (target instanceof Element && target.closest("a, button, [role='button']")) {
                haptics.click();
            }
        };

        document.addEventListener("mouseenter", handleMouseEnter, true);
        document.addEventListener("click", handleClick, true);

        return () => {
            document.removeEventListener("mouseenter", handleMouseEnter, true);
            document.removeEventListener("click", handleClick, true);
            window.removeEventListener("touchstart", handleUnlock, true);
            window.removeEventListener("mousedown", handleUnlock, true);
        };
    }, []);

    const handleKonami = useCallback(() => {
        if (konamiActive) return;

        setKonamiActive(true);
        document.documentElement.setAttribute("data-konami", "true");

        timerRef.current = setTimeout(() => {
            setKonamiActive(false);
            document.documentElement.removeAttribute("data-konami");
        }, 6000);
    }, [konamiActive]);

    useKonamiCode(handleKonami);

    // Clean up timer on unmount
    useEffect(() => {
        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
        };
    }, []);

    return (
        <>
            {children}
            {konamiActive && (
                <div
                    style={{
                        position: "fixed",
                        bottom: "80px",
                        left: "50%",
                        transform: "translateX(-50%)",
                        background: "rgba(0, 0, 0, 0.7)",
                        backdropFilter: "blur(20px)",
                        WebkitBackdropFilter: "blur(20px)",
                        border: "1px solid rgba(50, 215, 75, 0.3)",
                        borderRadius: "16px",
                        padding: "16px 32px",
                        zIndex: 10000,
                        fontFamily: "monospace",
                        color: "#32D74B",
                        animation: "konamiFadeIn 0.4s ease-out",
                        whiteSpace: "nowrap",
                    }}
                >
                    {dictionary.global.developer_mode_unlocked}
                </div>
            )}
        </>
    );
}
